import { createApp } from 'vue/dist/vue.esm-bundler';
import { createRouter, createWebHistory } from 'vue-router'
import { initVueSentry } from '../sentry';
import { createI18n } from 'vue-i18n';
import { createPinia } from 'pinia';
import ru from '../../frontend/locales/ru.json';
import en from '../../frontend/locales/en.json';

// Import ActionCable channels
import '../channels';


import axios from 'axios';
import { createToast } from 'mosha-vue-toastify';
import 'mosha-vue-toastify/dist/style.css';

const messages = {
  ru,
  en
};

// Function to get current locale from meta tag or URL
const getCurrentLocale = () => {
  // Try to get from meta tag first (most reliable, set by Rails)
  const metaTag = document.querySelector('meta[name="current-locale"]');
  const metaLocale = metaTag?.getAttribute('content');
  if (metaLocale && (metaLocale === 'ru' || metaLocale === 'en')) {
    return metaLocale;
  }

  // Fallback to URL parsing
  const urlMatch = window.location.pathname.match(/^\/(en|ru)/);
  if (urlMatch && (urlMatch[1] === 'ru' || urlMatch[1] === 'en')) {
    return urlMatch[1];
  }

  // Default to ru
  return 'ru';
};

// Create i18n instance - will be initialized with correct locale when app is created
let i18n = null;
import { $vfm, VueFinalModal, ModalsContainer } from 'vue-final-modal';

import EventsApp from '../../frontend/components/events/EventsApp.vue';
import EventsList from '../../frontend/components/events/EventsList.vue';
import EventsCreate from '../../frontend/components/events/EventsCreate.vue';
import EventItemFull from '../../frontend/components/events/EventItemFull.vue';
import EventsEdit from '../../frontend/components/events/EventsEdit.vue';
import MyEvents from '../../frontend/components/events/MyEvents.vue';
import TournamentManagement from '@tournaments/TournamentManagement.vue';
import GlobalNotifications from '../../frontend/components/shared/GlobalNotifications.vue';
import RedeemButton from '../../frontend/components/roster/display/RedeemButton.vue';
import LocaleSwitcher from '../../frontend/components/shared/LocaleSwitcher.vue';
import RedeemModal from '../../frontend/components/modal/RedeemModal.vue';

// Avoid installing duplicate interceptors on remount
let axiosUnauthorizedInterceptorId = null;

// Create router for events
const createEventsRouter = () => {
  // Get current locale from meta tag or URL
  const currentLocale = document.querySelector('meta[name="current-locale"]')?.getAttribute('content') ||
                        window.location.pathname.match(/^\/(en|ru)/)?.[1] || 'ru';

  // Base path includes locale
  const basePath = `/${currentLocale}/events`;

  const routes = [
    {
      path: '/',
      name: 'list',
      component: EventsList
    },
    {
      path: '/create',
      name: 'new_event',
      component: EventsCreate
    },
    {
      path: '/:id',
      name: 'show_event',
      component: EventItemFull
    },
    {
      path: '/:id/edit',
      name: 'edit_event',
      component: EventsEdit
    },
    {
      path: '/:id/tournament',
      name: 'tournament_management',
      component: TournamentManagement
    },
    {
      path: '/my_events',
      name: 'my_events',
      component: MyEvents
    }
  ];

  return createRouter({
    history: createWebHistory(basePath),
    routes
  });
};

// Create Vue app for events
const createEventsApp = () => {
  // Create i18n instance with current locale (determined after DOM is loaded)
  if (!i18n) {
    i18n = createI18n({
      legacy: false,
      globalInjection: true,
      locale: getCurrentLocale(),
      fallbackLocale: 'en',
      messages,
      silentTranslationWarn: true,
      silentFallbackWarn: true
    });
  } else {
    // Update locale if it changed
    const currentLocale = getCurrentLocale();
    if (i18n.global.locale.value !== currentLocale) {
      i18n.global.locale.value = currentLocale;
    }
  }

  const app = createApp(EventsApp)
  const router = createEventsRouter()

  initVueSentry({ app, router, appName: 'events' })

  // Global axios handler: redirect to sign-in on 401
  if (axiosUnauthorizedInterceptorId !== null) {
    axios.interceptors.response.eject(axiosUnauthorizedInterceptorId);
    axiosUnauthorizedInterceptorId = null;
  }
  axiosUnauthorizedInterceptorId = axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      if (status === 401) {
        const loginPath = '/users/sign_in';
        // Prevent redirect loops if we're already on the login page
        if (typeof window !== 'undefined' && window.location?.pathname !== loginPath) {
          window.location.href = loginPath;
          // Return a pending promise to stop further error handling
          return new Promise(() => {});
        }
      }
      return Promise.reject(error);
    }
  );

  // Global properties
  app.config.globalProperties.$axios = axios
  app.config.globalProperties.$toast = createToast

  // Set up CSRF token for axios
  const setupCSRFToken = () => {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    if (csrfToken) {
      axios.defaults.headers.common['X-CSRF-Token'] = csrfToken;
    } else {
      console.warn('CSRF token not found');
    }
  };

  // Set up CSRF token immediately and also on DOM ready
  setupCSRFToken();
  document.addEventListener('DOMContentLoaded', setupCSRFToken);

  // Use router, i18n and pinia
  app.use(router)
  app.use(i18n)
  const pinia = createPinia();
  app.use(pinia)

  // Watch for route changes to update locale
  router.afterEach((to) => {
    const newLocale = getCurrentLocale();
    if (i18n.global.locale.value !== newLocale) {
      i18n.global.locale.value = newLocale;
    }
  });

  // Register vue-final-modal components
  app.component('VueFinalModal', VueFinalModal);
  app.component('ModalsContainer', ModalsContainer);
  app.component('RedeemModal', RedeemModal);

  // Register header action components
  app.component('RedeemButton', RedeemButton);
  app.component('LocaleSwitcher', LocaleSwitcher);

  // Register global notifications component
  app.component('GlobalNotifications', GlobalNotifications)

  return app
}

// Track the current app instance
let currentApp = null;

// Function to mount the app
const mountEventsApp = () => {
  const eventsContainer = document.getElementById('events');

  if (!eventsContainer) {
    return;
  }

  // If there's already an app mounted, unmount it first
  if (currentApp) {
    currentApp.unmount();
    currentApp = null;
  }

  // Create and mount new app (i18n will be created inside createEventsApp)
  currentApp = createEventsApp();
  currentApp.mount('#events');
};

// Initialize events app when DOM is ready
document.addEventListener('DOMContentLoaded', mountEventsApp);
